import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, switchMap, take } from 'rxjs';

import { environment } from '../../environments/environment';

import { SportState } from './sport/sport.state';
import { Sport } from './sport/sport.models';

/**
 * Interceptor to prepend the correct API base URL and version based on the current sport.
 * It excludes requests to the user API and asset files.
 */
export const apiInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const store = inject(Store);

    if (req.url.startsWith('./assets/') || req.url.startsWith('/assets/')) {
        // Do not modify asset requests
        return next(req);
    }

    // allow requests to the user api to go through unchanged
    if (req.url.includes(environment.userApi)) {
        const clonedRequest = req.clone({ withCredentials: true });
        return next(clonedRequest);
    }

    return store.select(SportState.selectors.currentSport).pipe(
        take(1),
        // eslint-disable-next-line complexity
        switchMap((currentSport: Sport | undefined) => {
            if (!currentSport) {
                console.error(`[ApiInterceptor] No current sport set`);
                return next(req);
            }

            const apiUrl = environment.apis?.[currentSport];
            const apiVersion = environment.apiVersion;

            if (!apiUrl || !apiVersion) {
                console.error(`[ApiInterceptor] Could not resolve base URL for sport: ${currentSport}`);
                return next(req);
            }

            // clean up potential trailing/leading slashes
            const base = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
            const version = apiVersion.endsWith('/') ? apiVersion.slice(0, -1) : apiVersion;
            const path = req.url.startsWith('/') ? req.url.slice(1) : req.url;

            const finalUrl = `${base}/${version}/${path}`;
            const clonedRequest = req.clone({ url: finalUrl, withCredentials: true });

            return next(clonedRequest);
        })
    );
};
